import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useXSpaces } from "@/hooks/useXSpaces";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, Clock, Users, ArrowLeft, ArrowRight, Twitter, LogIn, Bookmark, BookmarkCheck, Heart, Share2, Mail, Radio, Mic, Copy, Check, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFavoriteEvents } from "@/hooks/useFavoriteEvents";
import { useInteractionSounds } from "@/hooks/useInteractionSounds";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";

// Custom icons
const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

interface SavedSpace {
  id: string;
  space_id: number;
  space_title: string;
  space_title_ar: string | null;
  space_url: string | null;
  space_image: string | null;
  scheduled_at: string | null;
  created_at: string;
}

// SpaceCard component matching the homepage section design
const SpaceCard = ({ space, isRTL }: { space: any; isRTL: boolean }) => {
  const { i18n } = useTranslation();
  const { toggleFavorite, isFavorite } = useFavoriteEvents();
  const { playHoverSound, playFavoriteSound, playShareSound } = useInteractionSounds();
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const title = space.title?.[i18n.language as 'en' | 'ar'] || space.title?.en || '';
  const shareUrl = `https://visitevent.live/x-space/${space.dbId}`;
  const shareMessage = isRTL 
    ? `🎙️ بث مباشر على منصة X!\n\n📢 ${title}\n👥 ${space.attendees || 0} مشارك\n\n🔗 سجل الآن للانضمام:`
    : `🎙️ Live broadcast on X Platform!\n\n📢 ${title}\n👥 ${space.attendees || 0} participants\n\n🔗 Register now to join:`;
  const encodedShareMessage = encodeURIComponent(shareMessage);
  const encodedShareUrl = encodeURIComponent(shareUrl);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    playFavoriteSound(!isFavorite(space.id));
    
    if (!isFavorite(space.id)) {
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.7 },
        colors: ['#ff6b6b', '#ee5a5a', '#ff8787', '#ffc9c9'],
        shapes: ['circle'],
      });
    }
    
    await toggleFavorite(space.id, isRTL);
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    playShareSound(!showShareMenu);
    setShowShareMenu(!showShareMenu);
    setCopied(false);
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success(isRTL ? "تم نسخ الرابط" : "Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <motion.div 
      className="relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl overflow-hidden group"
      style={{
        boxShadow: '8px 8px 16px rgba(0,0,0,0.1), -8px -8px 16px rgba(255,255,255,0.8)',
      }}
      whileHover={{ y: -5, scale: 1.02 }}
      onMouseEnter={() => playHoverSound()}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Action Buttons */}
      <div className="absolute top-3 left-3 z-20 flex gap-2">
        <motion.button
          onClick={handleFavoriteClick}
          className={`p-2 rounded-full backdrop-blur-sm transition-all ${
            isFavorite(space.id)
              ? 'bg-red-500 text-white'
              : 'bg-white/80 text-gray-600 hover:bg-red-100 hover:text-red-500'
          }`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Heart className={`w-4 h-4 ${isFavorite(space.id) ? 'fill-current' : ''}`} />
        </motion.button>
        
        <div className="relative">
          <motion.button
            onClick={handleShareClick}
            className="p-2 rounded-full bg-white/80 text-gray-600 hover:bg-blue-100 hover:text-blue-500 backdrop-blur-sm transition-all"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Share2 className="w-4 h-4" />
          </motion.button>
          
          <AnimatePresence>
            {showShareMenu && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -10 }}
                className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl p-2 flex gap-2 z-30"
              >
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodedShareMessage}&url=${encodedShareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <XIcon />
                </a>
                <a
                  href={`https://t.me/share/url?url=${encodedShareUrl}&text=${encodedShareMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-blue-500"
                  onClick={(e) => e.stopPropagation()}
                >
                  <TelegramIcon />
                </a>
                <a
                  href={`https://wa.me/?text=${encodedShareMessage}%20${encodedShareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-green-500"
                  onClick={(e) => e.stopPropagation()}
                >
                  <WhatsAppIcon />
                </a>
                <a
                  href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodedShareMessage}%20${encodedShareUrl}`}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Mail className="w-4 h-4" />
                </a>
                <button
                  onClick={handleCopyLink}
                  className={`p-2 rounded-full transition-colors ${copied ? 'bg-green-100 text-green-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600'}`}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={space.image || "/images/x-spaces-default.jpg"} 
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/images/x-spaces-default.jpg";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute top-3 right-3 bg-gray-900 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
          <Twitter className="w-3 h-3" />
          X Space
        </div>
        {space.isLive && (
          <div className="absolute bottom-3 right-3 bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 animate-pulse shadow-lg">
            <Radio className="w-3.5 h-3.5" />
            {isRTL ? "بث مباشر" : "LIVE NOW"}
          </div>
        )}
        {space.isEnded && !space.isLive && (
          <div className="absolute bottom-3 right-3 bg-gray-600 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg">
            <CheckCircle className="w-3.5 h-3.5" />
            {isRTL ? "منتهية" : "ENDED"}
          </div>
        )}
        {space.attendees > 0 && !space.isEnded && (
          <div className="absolute bottom-3 left-3 bg-white/90 text-gray-800 px-2 py-1 rounded-full text-xs flex items-center gap-1">
            <Users className="w-3 h-3" />
            {space.attendees} {isRTL ? "مشارك" : "attendees"}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-bold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {title}
        </h3>
        
        <div className="space-y-2 text-sm text-muted-foreground mb-4">
          {space.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{space.date[i18n.language as 'en' | 'ar']}</span>
            </div>
          )}
          {space.startTime && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>
                {space.startTime[i18n.language as 'en' | 'ar']}
                {space.endTime && ` - ${space.endTime[i18n.language as 'en' | 'ar']}`}
              </span>
            </div>
          )}
        </div>
        
        {space.xSpaceUrl && (
          <a 
            href={space.xSpaceUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium"
          >
            <Twitter className="w-4 h-4" />
            {isRTL ? "انضم للمساحة" : "Join Space"}
          </a>
        )}
      </div>
    </motion.div>
  );
};

// Add Your Space Card
const AddSpaceCard = ({ isRTL }: { isRTL: boolean }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { playHoverSound } = useInteractionSounds();

  const handleClick = () => {
    if (!user) {
      navigate("/auth");
    } else {
      navigate("/packages");
    }
  };

  return (
    <motion.div
      className="relative bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/30 rounded-xl overflow-hidden cursor-pointer border-2 border-dashed border-blue-300 dark:border-blue-600 min-h-[280px] flex items-center justify-center"
      style={{
        boxShadow: '8px 8px 16px rgba(0,0,0,0.08), -8px -8px 16px rgba(255,255,255,0.9)',
      }}
      whileHover={{ y: -5, scale: 1.02 }}
      onMouseEnter={() => playHoverSound()}
      onClick={handleClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="text-center p-6">
        <motion.div 
          className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg"
          whileHover={{ rotate: 10 }}
        >
          <Mic className="w-10 h-10 text-white" />
        </motion.div>
        <h3 className="text-xl font-bold text-blue-700 dark:text-blue-300 mb-2">
          {isRTL ? "أضف مساحتك الصوتية" : "Add Your Space"}
        </h3>
        <p className="text-sm text-blue-600/70 dark:text-blue-400/70 mb-4">
          {isRTL ? "شارك مساحتك مع المجتمع" : "Share your space with the community"}
        </p>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full text-sm font-medium shadow-md">
          {isRTL ? "ابدأ الآن" : "Start Now"}
          {isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
        </div>
      </div>
    </motion.div>
  );
};

const XSpaces = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { data: xSpaceEvents = [], isLoading } = useXSpaces();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [registeredSpaces, setRegisteredSpaces] = useState<number[]>([]);
  const [savedSpaces, setSavedSpaces] = useState<SavedSpace[]>([]);
  const [registering, setRegistering] = useState<number | null>(null);
  const [savingSpace, setSavingSpace] = useState<number | null>(null);

  useEffect(() => {
    if (user) {
      fetchRegisteredSpaces();
      fetchSavedSpaces();
    }
  }, [user]);

  const fetchSavedSpaces = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from("saved_spaces")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setSavedSpaces(data as SavedSpace[]);
    }
  };

  const fetchRegisteredSpaces = async () => {
    if (!user) return;
    
    const { data: visitor } = await supabase
      .from("visitors")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle();

    if (visitor) {
      const { data: registrations } = await supabase
        .from("space_registrations")
        .select("space_id")
        .eq("visitor_id", visitor.id);

      if (registrations) {
        setRegisteredSpaces(registrations.map(r => r.space_id));
      }
    }
  };

  const handleJoinSpace = async (event: typeof xSpaceEvents[number]) => {
    if (!user) {
      navigate("/auth");
      return;
    }

    if (registeredSpaces.includes(event.id)) {
      // Already registered, open the space
      if (event.xSpaceUrl) {
        window.open(event.xSpaceUrl, "_blank");
      }
      return;
    }

    setRegistering(event.id);

    try {
      // Get or create visitor profile
      let { data: visitor } = await supabase
        .from("visitors")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!visitor) {
        const { data: newVisitor, error: visitorError } = await supabase
          .from("visitors")
          .insert({
            user_id: user.id,
            full_name: user.user_metadata?.full_name || user.email?.split("@")[0] || "User",
            email: user.email || "",
          })
          .select("id")
          .single();

        if (visitorError) throw visitorError;
        visitor = newVisitor;
      }

      // Register for the space
      const { error: regError } = await supabase
        .from("space_registrations")
        .insert({
          visitor_id: visitor.id,
          space_id: event.id,
          space_title: event.title.en,
        });

      if (regError) {
        if (regError.code === "23505") {
          toast.info(isRTL ? "أنت مسجل مسبقاً في هذه المساحة" : "You're already registered for this space");
        } else {
          throw regError;
        }
      } else {
        toast.success(isRTL ? "تم التسجيل بنجاح!" : "Successfully registered!");
        setRegisteredSpaces([...registeredSpaces, event.id]);
      }

      // Open the space
      if (event.xSpaceUrl) {
        window.open(event.xSpaceUrl, "_blank");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(isRTL ? "حدث خطأ أثناء التسجيل" : "Error during registration");
    } finally {
      setRegistering(null);
    }
  };

  const handleSaveSpace = async (event: typeof xSpaceEvents[number]) => {
    if (!user) {
      navigate("/auth");
      return;
    }

    setSavingSpace(event.id);
    const isSaved = savedSpaces.some(s => s.space_id === event.id);

    try {
      if (isSaved) {
        // Remove from saved
        const { error } = await supabase
          .from("saved_spaces")
          .delete()
          .eq("user_id", user.id)
          .eq("space_id", event.id);

        if (error) throw error;
        setSavedSpaces(prev => prev.filter(s => s.space_id !== event.id));
        toast.success(isRTL ? "تم إزالة المساحة من المحفوظات" : "Space removed from saved");
      } else {
        // Add to saved
        const { data, error } = await supabase
          .from("saved_spaces")
          .insert({
            user_id: user.id,
            space_id: event.id,
            space_title: event.title.en,
            space_title_ar: event.title.ar,
            space_url: event.xSpaceUrl,
            space_image: event.image
          })
          .select()
          .single();

        if (error) {
          if (error.code === "23505") {
            toast.info(isRTL ? "المساحة محفوظة مسبقاً" : "Space already saved");
          } else {
            throw error;
          }
        } else {
          setSavedSpaces(prev => [data as SavedSpace, ...prev]);
          toast.success(isRTL ? "تم حفظ المساحة للاستماع لاحقاً" : "Space saved for later");
        }
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error(isRTL ? "حدث خطأ" : "An error occurred");
    } finally {
      setSavingSpace(null);
    }
  };

  const isSpaceSaved = (spaceId: number) => savedSpaces.some(s => s.space_id === spaceId);

  return (
    <div className={`min-h-screen bg-gray-50 ${isRTL ? "rtl" : "ltr"}`}>
      <div className="container py-8">
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            {isRTL ? (
              <>
                العودة للرئيسية
                <ArrowRight className="w-4 h-4" />
              </>
            ) : (
              <>
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </>
            )}
          </Button>
          <div className="flex items-center gap-3">
            {!authLoading && !user && (
              <Button
                onClick={() => navigate("/auth")}
                variant="outline"
                className="flex items-center gap-2"
              >
                <LogIn className="w-4 h-4" />
                {isRTL ? "تسجيل الدخول" : "Login"}
              </Button>
            )}
            <div className="p-3 bg-gray-100 rounded-lg">
              <Twitter className="h-6 w-6 text-gray-900" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">
              {isRTL ? "مساحات منصة اكس" : "X Platform Spaces"}
            </h1>
          </div>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="all" className="flex items-center gap-2">
              <Twitter className="w-4 h-4" />
              {isRTL ? "جميع المساحات" : "All Spaces"}
            </TabsTrigger>
            <TabsTrigger value="saved" className="flex items-center gap-2">
              <BookmarkCheck className="w-4 h-4" />
              {isRTL ? "المحفوظة" : "Saved"} ({savedSpaces.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-64 bg-gray-200 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : xSpaceEvents.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <Twitter className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-700 mb-2">
                  {isRTL ? "لا توجد مساحات حالياً" : "No X Spaces Available"}
                </h2>
                <p className="text-gray-500">
                  {isRTL ? "سيتم إضافة مساحات منصة اكس قريباً" : "X Platform Spaces will be added soon"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {xSpaceEvents.map((event) => (
                  <SpaceCard key={event.id} space={event} isRTL={isRTL} />
                ))}
                <AddSpaceCard isRTL={isRTL} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="saved">
            {savedSpaces.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <Bookmark className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-700 mb-2">
                  {isRTL ? "لا توجد مساحات محفوظة" : "No Saved Spaces"}
                </h2>
                <p className="text-gray-500">
                  {isRTL 
                    ? "اضغط على أيقونة الحفظ في أي مساحة لحفظها للاستماع لاحقاً" 
                    : "Click the bookmark icon on any space to save it for later"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedSpaces.map((saved) => {
                  const event = xSpaceEvents.find(e => e.id === saved.space_id);
                  if (event) {
                    return <SpaceCard key={saved.id} space={event} isRTL={isRTL} />;
                  }
                  
                  // Fallback for saved spaces without matching event
                  return (
                    <motion.div
                      key={saved.id}
                      className="relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl overflow-hidden"
                      style={{
                        boxShadow: '8px 8px 16px rgba(0,0,0,0.1), -8px -8px 16px rgba(255,255,255,0.8)',
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={saved.space_image || "/images/x-spaces-default.jpg"}
                          alt={isRTL ? saved.space_title_ar || saved.space_title : saved.space_title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "/images/x-spaces-default.jpg";
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute top-3 right-3 bg-primary text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                          <BookmarkCheck className="w-3 h-3" />
                          {isRTL ? "محفوظ" : "Saved"}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-lg mb-2 line-clamp-2">
                          {isRTL ? saved.space_title_ar || saved.space_title : saved.space_title}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          {isRTL ? "تم الحفظ: " : "Saved: "}
                          {new Date(saved.created_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
                        </p>
                        {saved.space_url && (
                          <a 
                            href={saved.space_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-primary hover:underline text-sm font-medium"
                          >
                            <Twitter className="w-4 h-4" />
                            {isRTL ? "فتح المساحة" : "Open Space"}
                          </a>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default XSpaces;
